import * as XLSX from "xlsx";

/**
 * Export rent roll data to Excel format optimized for QuickBooks
 */
export function exportRentRollToExcel(rentRollData, selectedMonth) {
  try {
    // Prepare data for QuickBooks import
    // QuickBooks typically expects: Date, Description, Account, Amount, etc.
    const worksheetData = [
      // Header row
      [
        "Date",
        "Property Name",
        "Unit Number",
        "Tenant Name",
        "Tenant Email",
        "Monthly Rent",
        "Status",
        "Due Date",
        "Paid Date",
        "Amount Due",
        "Amount Paid",
        "Late Fee",
        "Payment Method",
        "Notes",
      ],
    ];

    // Add data rows
    rentRollData.forEach((item) => {
      const dueDate = item.dueDate ? formatDateForExcel(item.dueDate) : "";
      const paidDate = item.paidDate ? formatDateForExcel(item.paidDate) : "";

      worksheetData.push([
        selectedMonth
          ? formatDateForExcel(new Date(selectedMonth + "-01"))
          : formatDateForExcel(new Date()),
        item.propertyName || "",
        item.unitNumber || "",
        item.tenantName || "",
        item.tenantEmail || "",
        item.monthlyRent || 0,
        item.status || "",
        dueDate,
        paidDate,
        item.amountDue || 0,
        item.amountPaid || 0,
        item.lateFee || 0,
        item.paymentMethod || "",
        item.notes || "",
      ]);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    ws["!cols"] = [
      { wch: 12 }, // Date
      { wch: 20 }, // Property Name
      { wch: 12 }, // Unit Number
      { wch: 20 }, // Tenant Name
      { wch: 25 }, // Tenant Email
      { wch: 12 }, // Monthly Rent
      { wch: 12 }, // Status
      { wch: 12 }, // Due Date
      { wch: 12 }, // Paid Date
      { wch: 12 }, // Amount Due
      { wch: 12 }, // Amount Paid
      { wch: 10 }, // Late Fee
      { wch: 15 }, // Payment Method
      { wch: 30 }, // Notes
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Rent Roll");

    // Generate filename
    const monthLabel = selectedMonth
      ? new Date(selectedMonth + "-01").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
    const filename = `Rent_Roll_${monthLabel.replace(/\s+/g, "_")}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting rent roll to Excel:", error);
    throw error;
  }
}

/**
 * Export delinquency data to Excel format optimized for QuickBooks
 */
export function exportDelinquencyToExcel(delinquencyData, selectedMonth) {
  try {
    // Prepare data for QuickBooks import
    const worksheetData = [
      // Header row
      [
        "Date",
        "Property Name",
        "Unit Number",
        "Tenant Name",
        "Tenant Email",
        "Rent Amount",
        "Days Overdue",
        "Late Fee Amount",
        "Total Owed",
        "Last Contact Date",
        "Contact Method",
        "Notes",
      ],
    ];

    // Add data rows
    delinquencyData.forEach((item) => {
      const lastContact = item.lastContact
        ? formatDateForExcel(item.lastContact)
        : "";

      worksheetData.push([
        selectedMonth
          ? formatDateForExcel(new Date(selectedMonth + "-01"))
          : formatDateForExcel(new Date()),
        item.propertyName || "",
        item.unitNumber || "",
        item.tenantName || "",
        item.tenantEmail || "",
        item.rentAmount || 0,
        item.daysOverdue || 0,
        item.lateFees || 0,
        item.totalDue || 0,
        lastContact,
        item.contactMethod || "",
        item.notes || "",
      ]);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    ws["!cols"] = [
      { wch: 12 }, // Date
      { wch: 20 }, // Property Name
      { wch: 12 }, // Unit Number
      { wch: 20 }, // Tenant Name
      { wch: 25 }, // Tenant Email
      { wch: 12 }, // Rent Amount
      { wch: 12 }, // Days Overdue
      { wch: 12 }, // Late Fee Amount
      { wch: 12 }, // Total Owed
      { wch: 15 }, // Last Contact Date
      { wch: 15 }, // Contact Method
      { wch: 40 }, // Notes
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Delinquency");

    // Generate filename
    const monthLabel = selectedMonth
      ? new Date(selectedMonth + "-01").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
    const filename = `Delinquency_Report_${monthLabel.replace(
      /\s+/g,
      "_"
    )}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting delinquency to Excel:", error);
    throw error;
  }
}

/**
 * Export comprehensive financial report to Excel with multiple sheets for QuickBooks
 */
export function exportFinancialReportToExcel(
  rentRollData,
  delinquencyData,
  ownerStatements,
  selectedMonth
) {
  try {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Rent Roll
    if (rentRollData && rentRollData.length > 0) {
      const rentRollSheet = createRentRollSheet(rentRollData, selectedMonth);
      XLSX.utils.book_append_sheet(wb, rentRollSheet, "Rent Roll");
    }

    // Sheet 2: Delinquency
    if (delinquencyData && delinquencyData.length > 0) {
      const delinquencySheet = createDelinquencySheet(
        delinquencyData,
        selectedMonth
      );
      XLSX.utils.book_append_sheet(wb, delinquencySheet, "Delinquency");
    }

    // Sheet 3: Summary (for QuickBooks reconciliation)
    const summarySheet = createSummarySheet(
      rentRollData,
      delinquencyData,
      selectedMonth
    );
    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

    // Generate filename
    const monthLabel = selectedMonth
      ? new Date(selectedMonth + "-01").toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
    const filename = `Financial_Report_${monthLabel.replace(/\s+/g, "_")}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting financial report to Excel:", error);
    throw error;
  }
}

/**
 * Create rent roll worksheet
 */
function createRentRollSheet(rentRollData, selectedMonth) {
  const worksheetData = [
    [
      "Date",
      "Property Name",
      "Unit Number",
      "Tenant Name",
      "Tenant Email",
      "Monthly Rent",
      "Status",
      "Due Date",
      "Paid Date",
      "Amount Due",
      "Amount Paid",
      "Late Fee",
      "Payment Method",
      "Notes",
    ],
  ];

  rentRollData.forEach((item) => {
    const dueDate = item.dueDate ? formatDateForExcel(item.dueDate) : "";
    const paidDate = item.paidDate ? formatDateForExcel(item.paidDate) : "";

    worksheetData.push([
      selectedMonth
        ? formatDateForExcel(new Date(selectedMonth + "-01"))
        : formatDateForExcel(new Date()),
      item.propertyName || "",
      item.unitNumber || "",
      item.tenantName || "",
      item.tenantEmail || "",
      item.monthlyRent || 0,
      item.status || "",
      dueDate,
      paidDate,
      item.amountDue || 0,
      item.amountPaid || 0,
      item.lateFee || 0,
      item.paymentMethod || "",
      item.notes || "",
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  ws["!cols"] = [
    { wch: 12 },
    { wch: 20 },
    { wch: 12 },
    { wch: 20 },
    { wch: 25 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 15 },
    { wch: 30 },
  ];
  return ws;
}

/**
 * Create delinquency worksheet
 */
function createDelinquencySheet(delinquencyData, selectedMonth) {
  const worksheetData = [
    [
      "Date",
      "Property Name",
      "Unit Number",
      "Tenant Name",
      "Tenant Email",
      "Rent Amount",
      "Days Overdue",
      "Late Fee Amount",
      "Total Owed",
      "Last Contact Date",
      "Contact Method",
      "Notes",
    ],
  ];

  delinquencyData.forEach((item) => {
    const lastContact = item.lastContact
      ? formatDateForExcel(item.lastContact)
      : "";

    worksheetData.push([
      selectedMonth
        ? formatDateForExcel(new Date(selectedMonth + "-01"))
        : formatDateForExcel(new Date()),
      item.propertyName || "",
      item.unitNumber || "",
      item.tenantName || "",
      item.tenantEmail || "",
      item.rentAmount || 0,
      item.daysOverdue || 0,
      item.lateFees || 0,
      item.totalDue || 0,
      lastContact,
      item.contactMethod || "",
      item.notes || "",
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  ws["!cols"] = [
    { wch: 12 },
    { wch: 20 },
    { wch: 12 },
    { wch: 20 },
    { wch: 25 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 15 },
    { wch: 15 },
    { wch: 40 },
  ];
  return ws;
}

/**
 * Create summary worksheet for QuickBooks reconciliation
 */
function createSummarySheet(rentRollData, delinquencyData, selectedMonth) {
  const totalRent =
    rentRollData?.reduce((sum, item) => sum + (item.monthlyRent || 0), 0) || 0;
  const totalCollected =
    rentRollData?.reduce((sum, item) => sum + (item.amountPaid || 0), 0) || 0;
  const totalDue =
    rentRollData?.reduce((sum, item) => sum + (item.amountDue || 0), 0) || 0;
  const totalLateFees = [
    ...(rentRollData || []),
    ...(delinquencyData || []),
  ].reduce((sum, item) => sum + (item.lateFee || item.lateFees || 0), 0);
  const totalDelinquent =
    delinquencyData?.reduce((sum, item) => sum + (item.totalDue || 0), 0) || 0;

  const worksheetData = [
    ["Financial Summary for QuickBooks Import"],
    [""],
    [
      "Report Period",
      selectedMonth ||
        new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
    ],
    ["Generated Date", formatDateForExcel(new Date())],
    [""],
    ["Income"],
    ["Total Monthly Rent", totalRent],
    ["Total Collected", totalCollected],
    ["Total Outstanding", totalDue],
    [""],
    ["Fees"],
    ["Total Late Fees", totalLateFees],
    [""],
    ["Delinquency"],
    ["Total Delinquent Amount", totalDelinquent],
    ["Number of Delinquent Doors", delinquencyData?.length || 0],
    [""],
    ["Summary"],
    ["Net Income (Collected + Late Fees)", totalCollected + totalLateFees],
    ["Outstanding Receivables", totalDue],
  ];

  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  ws["!cols"] = [{ wch: 30 }, { wch: 20 }];
  return ws;
}

/**
 * Format date for Excel (YYYY-MM-DD format)
 */
function formatDateForExcel(date) {
  if (!date) return "";

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
